import type { SceneConfig } from './sceneConfig';
import { resolveCdnUrl } from '../utils/cdnUrl';
import { MUZDALIFA_DEFAULT_COLLIDERS } from './collision/muzdalifaColliders';

const MUZDALIFA_2M_URL = resolveCdnUrl('models/muzdalifa/Muzdalifa_v4_2000k.sog');
const MUZDALIFA_1M_URL = resolveCdnUrl('models/muzdalifa/Muzdalifa_v4_1000k.sog');
const MUZDALIFA_600K_URL = resolveCdnUrl('models/muzdalifa/Muzdalifa_v4_600k.sog');

export const muzdalifaSceneConfig: SceneConfig = {
  id: 'muzdalifah',
  renderer: {
    clearColor: 0x1c1c1c,
  },
  camera: {
    fov: 60,
    near: 0.01,
    far: 1e4,
    initialPosition: { x: 0.38, y: 1.15, z: 3.08 },
    previewTourStart: { x: -0.07, y: 1.89, z: 5.05 },
    previewTourEnd: {
      position: { x: -7.5, y: 1.23, z: 1.38 },
      target: { x: -1.27, y: 0.17, z: 0.39 },
    },
    // Authored via the FlyoverTuner panel (?flight).
    flyover: {
      delay: 0.5,
      segments: [
        { type: 'waypoint', position: { x: -1.814, y: 0.7, z: -3.036 }, target: { x: -1.27, y: 0.17, z: 0.39 }, duration: 2.5 },
        { type: 'waypoint', position: { x: 4.96, y: 1.23, z: -0.6 }, target: { x: -1.27, y: 0.17, z: 0.39 }, duration: 2.5 },
      ],
    },
  },
  poi: {
    clusterEnabled: false,
  },
  controls: {
    orbitMinDistance: 0,
    orbitMaxDistance: 10000,
    clampMinHeight: 0.7,
    clampMaxHeight: Infinity,
    clampMaxHeightFar: Infinity,
    orbitMinPolarAngle: (70 / 180) * Math.PI,
    orbitMaxPolarAngle: Math.PI,
    constrainVertical: false,
    orbitCenter: { x: -1.27, y: 0.17, z: 0.39 },
  },
  colliders: MUZDALIFA_DEFAULT_COLLIDERS,
  splat: {
    defaultByQuality: {
      desktop: 'muzdalifa_2m',
      mobile: {
        high: 'muzdalifa_1m',
        middle: 'muzdalifa_1m',
        low: 'muzdalifa_600k',
      },
    },
    variants: [
      { key: 'muzdalifa_2m', url: MUZDALIFA_2M_URL },
      { key: 'muzdalifa_1m', url: MUZDALIFA_1M_URL },
      { key: 'muzdalifa_600k', url: MUZDALIFA_600K_URL },
    ],
    revealAnimationDuration: 3.5,
    revealDistanceScale: 1.5,
    revealMode: '3d',
    revealOrigin: { x: -1.27, y: 0.17, z: 0.39 },
  },
  previewModel: {
    url: resolveCdnUrl('preview/Muzdalifa_preloader_v2_pnts_150000_meshopt(16).glb'),
    style: {
      // Match pre_loader_II baseline (~3px on-screen). 0.8 давал sub-pixel
      // coverage на retina (gl_PointSize клипается в 1.0 при 0.8 × sizeFromMat=1),
      // что воспринималось как "бледно" — точки занимали 1 device-pixel.
      pointSizeFactor: 3.0,
      wavesCenter: { x: -1.27, y: 0.17, z: 0.39 },
      wavesAmplitude: 2.0,
      wavesPeriod: 1.0,
      wavesSpeed: 1.5,
      wavesColorIntensity: 5.0,
      wavesDisplacement: 0.02,
      // Use baked vertex colors as-is (matches pre_loader_II reference look).
      // Previous boost of 5.0 was clamping every baked highlight (vColor > 0.2)
      // to 1.0 via the fragment-shader clamp, killing the baked light/shadow
      // detail entirely — the very "lit" feel the reviewer asked for.
      colorMultiplier: 1.0,
    },
  },
  assistant: {
    position: { x: 0, y: 0, z: -0.03 },
    rotationY: 0,
    scale: 0.0000625,
    reflectionResolution: 128,
  },
  environment: {
    hemisphereLight: {
      skyColor: 0xffffff,
      groundColor: 0x444444,
      intensity: 0.6,
    },
    directionalLights: [
      {
        color: 0xffffff,
        intensity: 1.0,
        position: { x: 50, y: 100, z: 50 },
      },
    ],
    background: {
      // Night scene — solid dark background, no skybox. Splat data already
      // contains baked night colors with thousands of warm tent lights;
      // overlaying a daytime skybox bleeds through low-alpha splat regions
      // and washes the whole image cold blue/grey.
      color: 0x1c1c1c,
    },
  },
  virtualTour: [],
};
